import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { LoadingSpinner } from './LoadingSpinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary-600 text-white',
    'hover:bg-primary-700 active:bg-primary-800',
    'shadow-sm shadow-primary-600/20',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
  ),
  secondary: cn(
    'bg-white text-gray-700 border border-gray-300',
    'hover:bg-gray-50 active:bg-gray-100',
    'shadow-sm',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
  ),
  ghost: cn(
    'bg-transparent text-gray-600',
    'hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
  ),
  danger: cn(
    'bg-status-critical text-white',
    'hover:bg-red-600 active:bg-red-700',
    'shadow-sm shadow-red-500/20',
    'focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2'
  ),
  outline: cn(
    'bg-transparent text-gray-700 border border-gray-300',
    'hover:bg-gray-50 active:bg-gray-100',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
  ),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-base gap-2',
};

const iconSizes: Record<ButtonSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center font-medium',
          'rounded-lg transition-all duration-150 ease-out',
          'select-none whitespace-nowrap',
          // Variant and size
          variantStyles[variant],
          sizeStyles[size],
          // States
          fullWidth && 'w-full',
          isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          className
        )}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner
              size={iconSizes[size]}
              className={variant === 'primary' || variant === 'danger' ? 'text-white/70' : 'text-gray-400'}
            />
            <span className="opacity-70">{children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
